import { useState } from "react";
import { Bookmark, Flag, Heart, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { postJson } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const REASONS = ["spam", "ofensivo", "fraude", "otro"];

/** Share dropdown. Defaults to a wall post; pass url/text/testid for other content (e.g. news). */
export const ShareMenu = ({ post, url: shareUrl, text: shareText, testid }) => {
  const { t } = useLang();
  const url = shareUrl ?? `${window.location.origin}/comunidad/pared/${post.id}`;
  const text = shareText ?? `${post.author?.name}: ${post.body.slice(0, 120)}`;
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success(t("share.copied"));
  };
  const targets = [
    ["whatsapp", "WhatsApp", `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`],
    ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`],
    ["x", "X", `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`],
  ];
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className="post-act" aria-label={t("share.share")} data-testid={testid ?? `wall-share-${post.id}`}><Share2 size={15} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-white/10 bg-[#15130f] text-[#efe8dc]">
        {targets.map(([k, label, href]) => <DropdownMenuItem key={k} onSelect={() => open(href)} data-testid={`share-${k}-${post.id}`}>{label}</DropdownMenuItem>)}
        <DropdownMenuItem onSelect={copy} data-testid={`share-copy-${post.id}`}><Link2 size={14} className="mr-2" /> {t("share.copy")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ReportDialog = ({ post, open, setOpen, kind = "post" }) => {
  const { t } = useLang();
  const [reason, setReason] = useState("spam");
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      await postJson(`/community/${kind}/${post.id}/report`, { reason, note });
      toast.success(t("report.thanks"));
      setOpen(false);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm border-white/10 bg-[#15130f] text-[#efe8dc]" data-testid={`report-dialog-${post.id}`}>
        <DialogTitle className="font-display text-2xl font-normal">{t("report.title")}</DialogTitle>
        <div className="mt-2 space-y-2">
          {REASONS.map((r) => (
            <label key={r} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-sm hover:bg-white/5">
              <input type="radio" name={`reason-${post.id}`} checked={reason === r} onChange={() => setReason(r)} data-testid={`report-reason-${r}`} /> {t(`report.${r}`)}
            </label>
          ))}
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} placeholder={t("report.notePh")} className="support-input resize-none" data-testid="report-note-input" />
        </div>
        <button type="button" onClick={send} className="btn-primary mt-3 w-full justify-center" data-testid="report-submit-btn">{t("report.send")}</button>
      </DialogContent>
    </Dialog>
  );
};

/** Like · save · share · report row for a wall post. */
export const PostActions = ({ post, liked, saved, onLike, onSave, canReport, needLogin }) => {
  const { t } = useLang();
  const [reporting, setReporting] = useState(false);
  return (
    <div className="mt-3 flex items-center gap-1">
      <button type="button" onClick={() => onLike(post.id)} aria-pressed={liked} className={`post-act ${liked ? "text-[#ff6b8a]" : ""}`} data-testid={`wall-like-${post.id}`}>
        <Heart size={15} fill={liked ? "currentColor" : "none"} /> <span data-testid={`wall-like-count-${post.id}`}>{post.like_count ?? 0}</span>
      </button>
      <button type="button" onClick={() => onSave(post.id)} aria-pressed={saved} aria-label={t(saved ? "save.saved" : "save.save")} title={t(saved ? "save.saved" : "save.save")} className={`post-act ${saved ? "text-[var(--green)]" : ""}`} data-testid={`wall-save-${post.id}`}>
        <Bookmark size={15} fill={saved ? "currentColor" : "none"} />
      </button>
      <ShareMenu post={post} />
      {canReport && (
        <button type="button" onClick={() => (needLogin ? needLogin() : setReporting(true))} className="post-act ml-auto" aria-label={t("report.title")} title={t("report.title")} data-testid={`wall-report-${post.id}`}><Flag size={14} /></button>
      )}
      {reporting && <ReportDialog post={post} open={reporting} setOpen={setReporting} />}
    </div>
  );
};
